import * as Header from '-/Custom/Header';
import capitalize from 'lodash.capitalize';
import type { ComponentProps } from 'react';
import { useEffect, useState } from 'react';
import { useFieldArray, useFormContext, useWatch } from 'react-hook-form';
import { StyleSheet } from 'react-native-unistyles';
import Box from '@/components/UI/Box';
import Row from '@/components/UI/Row';
import Text from '@/components/UI/Text';
import TextInput from '@/components/UI/TextInput';
import getEmptyEntity from '@/utils/getEmptyEntity';
import getRandomColor from '@/utils/getRandomColor';
import singularize from '@/utils/singularize';
import Icon from '../Unistyles/Icon';
import type { EntityListValues } from './EntityList.types';
export function Container(props: ComponentProps<typeof Box>) {
  return (
    <Box
      direction="horizontal"
      alignItems="center"
      py="xs"
      px="md"
      justifyContent="space-between"
      {...props}
    />
  );
}
export const Input = function Input({
  name,
  maxEntities,
}: {
  name: string;
  maxEntities: number;
}) {
  const { getValues, setValue, control } = useFormContext<EntityListValues>();
  const formEntities = useWatch({
    name,
    control,
  });
  const { append } = useFieldArray({
    name,
    control,
  });
  const [numberOfEntities, setNumberOfEntities] = useState<number>(
    getValues(name)?.length || 0,
  );
  useEffect(() => {
    setNumberOfEntities(formEntities.length || 0);
  }, [formEntities.length]);
  const setEntityValues = () => {
    const entities = getValues(name);
    setValue(
      name,
      [
        ...(entities || []).slice(0, Math.max(numberOfEntities, 0)),
        ...Array.from(
          {
            length: Math.max(numberOfEntities - (entities?.length || 0), 0),
          },
          () => getEmptyEntity(getRandomColor()),
        ),
      ],
      {
        shouldValidate: true,
      },
    );
    setNumberOfEntities(Math.max(numberOfEntities, 0));
  };
  return (
    <Row
      spaced
      style={{
        paddingHorizontal: 0,
        paddingVertical: 0,
      }}
    >
      <Box direction="horizontal" align="center">
        <Header.Button
          onPress={() => {
            const entities = getValues(name);
            setValue(name, entities?.slice(0, entities.length - 1), {
              shouldDirty: true,
              shouldValidate: true,
            });
            setNumberOfEntities(Math.max(numberOfEntities - 1, 0));
          }}
        >
          <Icon name="remove-outline" />
        </Header.Button>
        <TextInput
          variant="plain"
          size="xl"
          width={64}
          inputMode="numeric"
          enterKeyHint="done"
          maxLength={2}
          placeholder="0"
          textAlign="center"
          value={numberOfEntities.toString()}
          onBlur={() => {
            setEntityValues();
          }}
          onKeyPress={(event) => {
            if (event.nativeEvent.key === 'Enter') {
              event.preventDefault();
              setEntityValues();
            }
          }}
          onChangeText={(text) => {
            let num = Number.parseInt(text, 10);
            if (num < 0) {
              num = 0;
            }
            if (num > maxEntities) {
              num = maxEntities;
            }
            setNumberOfEntities(num || 0);
          }}
          style={style.countInput}
        />
        <Header.Button
          onPress={() => {
            const entities = getValues(name);
            if ((entities?.length || 0) < maxEntities) {
              append(getEmptyEntity(getRandomColor()));
              setNumberOfEntities(
                (currentNumberOfEntities) => currentNumberOfEntities + 1,
              );
            }
          }}
        >
          <Icon name="add-outline" />
        </Header.Button>
      </Box>
    </Row>
  );
};
const style = StyleSheet.create((theme) => ({
  countInput: {
    fontWeight: theme.fontWeight.semibold,
  },
}));
export const Heading = function Heading({ name }: { name: string }) {
  const { control } = useFormContext<EntityListValues>();
  const items = useWatch({
    name,
    control,
  });
  const filledSlots = items?.filter((item) => item.title).length;
  const isComplete = filledSlots === items?.length;
  const pluralLabel = capitalize(name);
  const prompt = `Fill out all ${singularize(name)} slots to continue.`;
  return (
    <Box direction="vertical" flex>
      <Text size="lg">
        {items?.length
          ? `${filledSlots}/${items?.length} ${pluralLabel}`
          : `How Many ${pluralLabel}?`}
      </Text>
      {!isComplete ? (
        <Text fontWeight="normal" variant="warning" size="sm">
          {prompt}
        </Text>
      ) : null}
    </Box>
  );
};
